import type { ResultPropertyId, ResultRangeMethod, ResultRangeScope } from './resultProperties';
import {
  normalizeFracSimNavigation,
  type FracSimSubSection,
  type FracSimWorkflowStep,
} from './fracSimNavigation';

export type FracSimPersistedState = {
  designId: number | null;
  simulationId: number | null;
  streamJobId: string | null;
  reservoirPreset: string;
  resultProperty: ResultPropertyId;
  rangeScope: ResultRangeScope;
  rangeMethod: ResultRangeMethod;
  manualMin: string;
  manualMax: string;
  naturalFractureModelId: number | null;
  naturalFracturePhysics: boolean;
  naturalFractureDisplay: boolean;
  naturalFractureColor: string;
  interactionPointDisplay: boolean;
  optimizationVariables: string[];
  topN: 3 | 5 | 10;
  comparisonSchemeIds: string[];
  designMode: 'MANUAL' | 'GEOLOGY_CONSTRAINED';
  workflowStep: 'DESIGN_BASIS' | 'SENSITIVITY' | 'OPTIMIZATION' | 'COMPARISON' | 'FULL_WELL';
  activeWorkflowStep: FracSimWorkflowStep;
  activeSubSection: FracSimSubSection;
  engineeringConstraints: Record<string, { value: number | boolean; unit: string; source: string }>;
  objectiveTemplate: 'BALANCED_STIMULATION' | 'LENGTH_PRIORITY' | 'CONTAINMENT_PRIORITY' | 'CLUSTER_BALANCE_PRIORITY';
  simulationOptions: Record<string, unknown>;
};

export const DEFAULT_FRACSIM_PERSISTED_STATE: FracSimPersistedState = {
  designId: null,
  simulationId: null,
  streamJobId: null,
  reservoirPreset: '',
  resultProperty: 'dynamic_width',
  rangeScope: 'GLOBAL',
  rangeMethod: 'MIN_MAX',
  manualMin: '0',
  manualMax: '10',
  naturalFractureModelId: null,
  naturalFracturePhysics: false,
  naturalFractureDisplay: false,
  naturalFractureColor: '#00A6A6',
  interactionPointDisplay: false,
  optimizationVariables: ['cluster_spacing_m', 'pump_rate_m3min'],
  topN: 5,
  comparisonSchemeIds: [],
  designMode: 'MANUAL',
  workflowStep: 'DESIGN_BASIS',
  activeWorkflowStep: 'SIMULATION',
  activeSubSection: '结果分析',
  engineeringConstraints: {},
  objectiveTemplate: 'BALANCED_STIMULATION',
  simulationOptions: {},
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

const storageKey = (projectId: number) => `fracsight:fracsim:v1.8:project:${projectId}`;
const legacyStorageKey = (projectId: number) => `fracsight:fracsim:v1.7:project:${projectId}`;

const finiteId = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const loadFracSimState = (
  projectId: number | undefined,
  storage: StorageLike | undefined = typeof window === 'undefined' ? undefined : window.localStorage,
): FracSimPersistedState => {
  if (!projectId || !storage) return { ...DEFAULT_FRACSIM_PERSISTED_STATE };
  try {
    const raw = storage.getItem(storageKey(projectId)) ?? storage.getItem(legacyStorageKey(projectId));
    if (!raw) return { ...DEFAULT_FRACSIM_PERSISTED_STATE };
    const value = JSON.parse(raw) as Partial<FracSimPersistedState>;
    const navigation = normalizeFracSimNavigation(
      value.activeWorkflowStep,
      value.activeSubSection,
      value.workflowStep,
    );
    return {
      ...DEFAULT_FRACSIM_PERSISTED_STATE,
      ...value,
      designId: finiteId(value.designId),
      simulationId: finiteId(value.simulationId),
      streamJobId: typeof value.streamJobId === 'string' && value.streamJobId.length > 0 ? value.streamJobId : null,
      naturalFractureModelId: finiteId(value.naturalFractureModelId),
      naturalFractureColor: typeof value.naturalFractureColor === 'string'
        && /^#[0-9a-f]{6}$/i.test(value.naturalFractureColor)
        ? value.naturalFractureColor : DEFAULT_FRACSIM_PERSISTED_STATE.naturalFractureColor,
      resultProperty: typeof value.resultProperty === 'string' ? value.resultProperty as ResultPropertyId : 'dynamic_width',
      optimizationVariables: Array.isArray(value.optimizationVariables)
        ? value.optimizationVariables.filter((item): item is string => typeof item === 'string').slice(0, 3)
        : [...DEFAULT_FRACSIM_PERSISTED_STATE.optimizationVariables],
      comparisonSchemeIds: Array.isArray(value.comparisonSchemeIds)
        ? value.comparisonSchemeIds.filter((item): item is string => typeof item === 'string').slice(0, 5)
        : [],
      designMode: value.designMode === 'GEOLOGY_CONSTRAINED' ? 'GEOLOGY_CONSTRAINED' : 'MANUAL',
      workflowStep: ['DESIGN_BASIS', 'SENSITIVITY', 'OPTIMIZATION', 'COMPARISON', 'FULL_WELL'].includes(String(value.workflowStep))
        ? value.workflowStep as FracSimPersistedState['workflowStep'] : 'DESIGN_BASIS',
      ...navigation,
      engineeringConstraints: value.engineeringConstraints && typeof value.engineeringConstraints === 'object'
        ? value.engineeringConstraints as FracSimPersistedState['engineeringConstraints'] : {},
      objectiveTemplate: ['BALANCED_STIMULATION', 'LENGTH_PRIORITY', 'CONTAINMENT_PRIORITY', 'CLUSTER_BALANCE_PRIORITY'].includes(String(value.objectiveTemplate))
        ? value.objectiveTemplate as FracSimPersistedState['objectiveTemplate'] : 'BALANCED_STIMULATION',
      simulationOptions: value.simulationOptions && typeof value.simulationOptions === 'object'
        ? value.simulationOptions as Record<string, unknown> : {},
      topN: value.topN === 3 || value.topN === 10 ? value.topN : 5,
    };
  } catch {
    return { ...DEFAULT_FRACSIM_PERSISTED_STATE };
  }
};

export const saveFracSimState = (
  projectId: number | undefined,
  value: FracSimPersistedState,
  storage: StorageLike | undefined = typeof window === 'undefined' ? undefined : window.localStorage,
): void => {
  if (!projectId || !storage) return;
  storage.setItem(storageKey(projectId), JSON.stringify(value));
};
